
import { Pagination } from "@/components/ui/pagination"
import { useTheme } from "@/contexts/ThemeContext"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"

interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  totalItems?: number;
  showPageSizeSelect?: boolean;
  compact?: boolean;
}

export function TablePagination({ 
  currentPage, 
  totalPages, 
  onPageChange,
  pageSize = 50,
  onPageSizeChange,
  totalItems,
  showPageSizeSelect = false,
  compact = false
}: TablePaginationProps) {
  console.log(`TablePagination rendered: currentPage=${currentPage}, totalPages=${totalPages}, pageSize=${pageSize}`);
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  
  // Handle page click
  const handlePageClick = (pageNum: number) => {
    console.log(`Page ${pageNum} clicked`);
    onPageChange(pageNum);
  };

  // Handle page size change
  const handlePageSizeChange = (value: string) => {
    const newSize = value === 'all' ? -1 : parseInt(value);
    console.log(`Page size changed to: ${newSize}`);
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  const startItem = Math.min((currentPage - 1) * (pageSize === -1 ? totalItems || 0 : pageSize) + 1, totalItems || 0);
  const endItem = pageSize === -1 ? totalItems || 0 : Math.min(currentPage * pageSize, totalItems || 0);
  
  // Don't show pagination controls when "Show All" is selected
  const showPaginationControls = pageSize !== -1 && totalPages > 1;

  if (compact) {
    return (
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-muted-foreground">
          {totalItems ? `${startItem}-${endItem} of ${totalItems}` : ''}
        </div>
        {showPaginationControls && (
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-sm font-medium">
              {currentPage} of {totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        {totalItems && (
          <div className="text-sm text-muted-foreground">
            Showing {startItem}-{endItem} of {totalItems} results
          </div>
        )}
        
        {showPageSizeSelect && onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows per page:</span>
            <Select
              value={pageSize === -1 ? 'all' : pageSize.toString()}
              onValueChange={handlePageSizeChange}
            >
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="1000">1000</SelectItem>
                <SelectItem value="all">Show All</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      
      {showPaginationControls && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageClick}
            className={isLightMode ? "text-gray-800" : ""}
            showFirstLast={totalPages > 10}
            showPageNumbers={totalPages <= 50}
          />
        </div>
      )}
    </div>
  );
}
